// API integration boundary. Every user action that changes data goes through
// this interface. Demo mode uses mockApi (no network). Api mode (VITE_DATA_MODE=api,
// VITE_API_BASE_URL=/api) uses HttpApi against the UGAIN API in server/ (same origin,
// HttpOnly session cookie, CSRF header on writes). Contract: server/src/routes.ts.
import type { Activity, ChatMessage, DemoData, Health, HomeTheme, PhotoKind, Privacy, PulseComment, ShareRecord, TimelineUpdate } from '../domain/types';
import { createSeed } from '../data/seed';
import { CSRF_HEADER, UNAUTHENTICATED_EVENT } from './session';

export interface UgainApi {
  /** GET /me/bootstrap: everything the signed-in ambassador may see. */
  bootstrap(): Promise<DemoData>;
  setTaskDone(taskId: string, done: boolean): Promise<void>;
  addTask(activityId: string, task: { title: string; ownerId: string; due: string }): Promise<string | null>;
  setHealth(activityId: string, health: Health): Promise<void>;
  publishUpdate(update: TimelineUpdate, opts: { postToPulse: boolean; notifyTeam: boolean }): Promise<void>;
  /** Returns the server id of the new activity (null in demo mode). */
  proposeActivity(activity: Activity, opts: { announce: boolean }): Promise<string | null>;
  decideProposal(activityId: string, decision: 'approve' | 'reject', reason: string): Promise<void>;
  postHelpRequest(activityId: string, roles: string[], details: string): Promise<void>;
  withdrawHelpRequest(activityId: string, role: string): Promise<void>;
  setOfferHelp(activityId: string, on: boolean): Promise<void>;
  acceptOffer(offerId: string): Promise<void>;
  setFollow(kind: 'activity' | 'ambassador', id: string, on: boolean): Promise<void>;
  sendMessage(message: ChatMessage): Promise<void>;
  addComment(pulseId: string, comment: PulseComment): Promise<void>;
  recordShare(record: ShareRecord): Promise<void>;
  updateProfile(personId: string, patch: { bio?: string; privacy?: Partial<Privacy>; homeTheme?: HomeTheme; unitId?: string; referralUrl?: string }): Promise<void>;
  /** Replace the member's own self-selected UNIT affiliations; `primary` is one of them, or '' for none. */
  setAffiliations(affiliations: string[], primary: string): Promise<void>;
  /** Upload (replace) one of the member's own photos. The image is already resized and re-encoded. */
  uploadPhoto(kind: PhotoKind, image: Blob): Promise<void>;
  removePhoto(kind: PhotoKind): Promise<void>;
}

/** Photos need the server: demo mode never pretends to store them. */
const noPhotos = async (): Promise<void> => {
  throw new ApiError(501, 'photos_need_sign_in');
};

const done = async (): Promise<void> => undefined;

export const mockApi: UgainApi = {
  bootstrap: async () => createSeed(),
  setTaskDone: done,
  addTask: async () => null,
  setHealth: done,
  publishUpdate: done,
  proposeActivity: async () => null,
  decideProposal: done,
  postHelpRequest: done,
  withdrawHelpRequest: done,
  setOfferHelp: done,
  acceptOffer: done,
  setFollow: done,
  sendMessage: done,
  addComment: done,
  recordShare: done,
  updateProfile: done,
  setAffiliations: done,
  uploadPhoto: noPhotos,
  removePhoto: noPhotos,
};

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** The rejected field, when the server names one (400 invalid_field). */
  readonly field?: string;
  constructor(status: number, code: string, field?: string) {
    super(`${status} ${code}`);
    this.status = status;
    this.code = code;
    this.field = field;
  }
}

export class HttpApi implements UgainApi {
  private readonly base: string;
  constructor(base: string) {
    this.base = base.replace(/\/$/, '');
  }

  private async send<T = void>(method: string, path: string, body?: unknown): Promise<T> {
    // Cookie session only: no Authorization header, no token in JS.
    const res = await fetch(`${this.base}${path}`, {
      method,
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...(body === undefined ? {} : { 'Content-Type': 'application/json' }), ...(method === 'GET' ? {} : CSRF_HEADER) },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (res.status === 401) window.dispatchEvent(new Event(UNAUTHENTICATED_EVENT));
    if (!res.ok) {
      const b = (await res.json().catch(() => ({}))) as { error?: string; field?: string };
      throw new ApiError(res.status, b.error ?? 'error', b.field);
    }
    return (res.status === 204 ? undefined : await res.json()) as T;
  }

  bootstrap() { return this.send<DemoData>('GET', '/me/bootstrap'); }
  setTaskDone(taskId: string, isDone: boolean) { return this.send('PATCH', `/tasks/${taskId}`, { done: isDone }); }
  async addTask(activityId: string, task: { title: string; ownerId: string; due: string }) { return (await this.send<{ id: string }>('POST', `/activities/${activityId}/tasks`, task)).id; }
  setHealth(activityId: string, health: Health) { return this.send('PATCH', `/activities/${activityId}`, { health }); }
  publishUpdate(u: TimelineUpdate, opts: { postToPulse: boolean; notifyTeam: boolean }) {
    return this.send('POST', `/activities/${u.activityId}/updates`, { text: u.text, visibility: u.visibility, health: u.health ?? null, mediaCount: u.mediaCount, ...opts });
  }
  async proposeActivity(a: Activity, opts: { announce: boolean }) {
    // Only the proposal fields are sent; the server sets id, stage, team, organiser and health.
    const activity = { name: a.name, type: a.type, series: a.series, city: a.city, country: a.country, timeZone: a.timeZone, startsAt: a.startsAt, place: a.place, needs: a.needs, brief: a.brief };
    return (await this.send<{ id: string }>('POST', '/activity-proposals', { activity, ...opts })).id;
  }
  decideProposal(activityId: string, decision: 'approve' | 'reject', reason: string) { return this.send('POST', `/admin/proposals/${activityId}/decision`, { decision, reason }); }
  postHelpRequest(activityId: string, roles: string[], details: string) { return this.send('POST', `/activities/${activityId}/help-requests`, { roles, details }); }
  withdrawHelpRequest(activityId: string, role: string) { return this.send('DELETE', `/activities/${activityId}/help-requests/${encodeURIComponent(role)}`); }
  setOfferHelp(activityId: string, on: boolean) { return this.send(on ? 'POST' : 'DELETE', `/activities/${activityId}/offers/me`, on ? {} : undefined); }
  acceptOffer(offerId: string) { return this.send('POST', `/offers/${offerId}/accept`); }
  setFollow(kind: 'activity' | 'ambassador', id: string, on: boolean) { return this.send(on ? 'PUT' : 'DELETE', `/me/follows/${kind}/${id}`); }
  sendMessage(message: ChatMessage) { return this.send('POST', `/activities/${message.activityId}/messages`, { text: message.text }); }
  addComment(pulseId: string, comment: PulseComment) { return this.send('POST', `/pulse/${pulseId}/comments`, { text: comment.text }); }
  recordShare(record: ShareRecord) { return this.send('POST', `/share-kits/${encodeURIComponent(record.kitId)}/records`, { platform: record.platform, url: record.url }); }
  // Self only: the server derives the member from the session (personId is not sent).
  updateProfile(_personId: string, patch: { bio?: string; privacy?: Partial<Privacy>; homeTheme?: HomeTheme; unitId?: string; referralUrl?: string }) { return this.send('PATCH', '/me', patch); }
  setAffiliations(affiliations: string[], primary: string) { return this.send('PUT', '/me/affiliations', { affiliations, primary: primary || null }); }
  async uploadPhoto(kind: PhotoKind, image: Blob) {
    const res = await fetch(`${this.base}/me/media/${kind}`, {
      method: 'PUT', credentials: 'same-origin', body: image,
      headers: { Accept: 'application/json', 'Content-Type': image.type, ...CSRF_HEADER },
    });
    if (res.status === 401) window.dispatchEvent(new Event(UNAUTHENTICATED_EVENT));
    if (!res.ok) throw new ApiError(res.status, ((await res.json().catch(() => ({}))) as { error?: string }).error ?? 'error');
  }
  removePhoto(kind: PhotoKind) { return this.send('DELETE', `/me/media/${kind}`); }
}

const env = import.meta.env;
/** True only when the HTTP client is really active (api mode AND a base URL). */
export const apiActive: boolean = env.VITE_DATA_MODE === 'api' && Boolean(env.VITE_API_BASE_URL);
export const api: UgainApi = apiActive ? new HttpApi(env.VITE_API_BASE_URL!) : mockApi;
